import { DateTime } from 'luxon';

import type { ChatSession } from '@/hooks/useAgentChatSessions';

/** A labelled bucket of conversations, ordered most-recent-first within. */
export type SessionGroup = {
  /** Display label, e.g. "Today". */
  label: string;
  sessions: ChatSession[];
};

const GROUP_LABELS = {
  today: 'Today',
  yesterday: 'Yesterday',
  past7Days: 'Previous 7 days',
  older: 'Older',
} as const;

export const groupSessionsByDate = (
  sessions: ChatSession[],
  now: DateTime = DateTime.now(),
): SessionGroup[] => {
  const startOfToday = now.startOf('day');
  const startOfYesterday = startOfToday.minus({ days: 1 });
  // "Previous 7 days" covers the 6 days before yesterday (today + yesterday
  // are their own buckets), i.e. anything on/after 7 days ago.
  const startOfPast7Days = startOfToday.minus({ days: 7 });

  const buckets: Record<keyof typeof GROUP_LABELS, ChatSession[]> = {
    today: [],
    yesterday: [],
    past7Days: [],
    older: [],
  };

  const sorted = [...sessions].sort(
    (a, b) =>
      DateTime.fromISO(b.updatedAt).toMillis() -
      DateTime.fromISO(a.updatedAt).toMillis(),
  );

  for (const session of sorted) {
    const updated = DateTime.fromISO(session.updatedAt);
    if (updated >= startOfToday) buckets.today.push(session);
    else if (updated >= startOfYesterday) buckets.yesterday.push(session);
    else if (updated >= startOfPast7Days) buckets.past7Days.push(session);
    else buckets.older.push(session);
  }

  return (Object.keys(GROUP_LABELS) as (keyof typeof GROUP_LABELS)[])
    .map((key) => ({ label: GROUP_LABELS[key], sessions: buckets[key] }))
    .filter((group) => group.sessions.length > 0);
};
